import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, Select, Button } from '../components/Common';
import { usePhysios } from '../hooks/usePhysios';
import { usePatients } from '../hooks/usePatients';
import { useAppointments } from '../hooks/useAppointments';
import { useConfirmSuggestion } from '../hooks/useCancellations';
import { adminApi } from '../services/api';
import { format, parseISO, isSameDay, startOfWeek, addDays } from 'date-fns';

// Fix Leaflet default marker icons
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Physio home marker (larger, with house icon)
const createPhysioHomeIcon = (color: string) => {
  return L.divIcon({
    className: 'physio-home-marker',
    html: `<div style="
      background-color: ${color};
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 3px 6px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
      </svg>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

// Numbered patient marker
const createNumberedPatientIcon = (number: number, color: string) => {
  return L.divIcon({
    className: 'numbered-patient-marker',
    html: `<div style="
      background-color: ${color};
      width: 26px;
      height: 26px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 12px;
    ">${number}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
};

// Highlighted patient marker (for cancellation context)
const createHighlightedPatientIcon = () => {
  return L.divIcon({
    className: 'highlighted-patient-marker',
    html: `<div style="
      background-color: #EF4444;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 3px solid #FCD34D;
      box-shadow: 0 0 12px rgba(239, 68, 68, 0.6), 0 3px 6px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      animation: pulse 2s infinite;
    ">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

// Physio colors
const physioColors = [
  '#3B82F6', // blue
  '#10B981', // green
  '#8B5CF6', // purple
  '#F59E0B', // amber
  '#EC4899', // pink
  '#14B8A6', // teal
];

interface Coordinates {
  lat: number;
  lng: number;
}

interface AppointmentWithCoords {
  id: number;
  patient_id: number;
  patient_name: string;
  patient_postcode: string;
  physio_id: number;
  physio_name: string;
  start_time: string;
  end_time: string;
  coords: Coordinates | null;
}

// Component to fit map bounds
function FitBounds({ bounds }: { bounds: L.LatLngBounds | null }) {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, bounds]);

  return null;
}

export function Map() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: physios } = usePhysios();
  const { data: patients } = usePatients();
  const { data: appointments } = useAppointments();
  const confirmSuggestion = useConfirmSuggestion();

  // Parse URL params for cancellation context
  const urlPhysioId = searchParams.get('physioId');
  const urlDate = searchParams.get('date');
  const urlPatientId = searchParams.get('patientId');
  const urlTime = searchParams.get('time');
  const urlPatientName = searchParams.get('patientName');
  const urlCancellationId = searchParams.get('cancellationId');
  const urlSuggestionId = searchParams.get('suggestionId');

  const [selectedPhysio, setSelectedPhysio] = useState<number | 'all'>('all');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [physioCoords, setPhysioCoords] = useState<Record<number, Coordinates | null>>({});
  const [patientCoords, setPatientCoords] = useState<Record<number, Coordinates | null>>({});
  const [routeCoords, setRouteCoords] = useState<Array<[number, number]>>([]);
  const [isLoadingCoords, setIsLoadingCoords] = useState(false);
  const [routeInfo, setRouteInfo] = useState<{ distance_km: number; duration_mins: number } | null>(null);
  const [highlightedPatientCoords, setHighlightedPatientCoords] = useState<Coordinates | null>(null);

  // Apply URL params on mount
  useEffect(() => {
    if (urlPhysioId) {
      setSelectedPhysio(parseInt(urlPhysioId));
    }
    if (urlDate) {
      setSelectedDate(parseISO(urlDate));
    }
  }, [urlPhysioId, urlDate]);

  // Load highlighted patient coordinates
  useEffect(() => {
    if (!urlPatientId || !patients) return;

    const patient = patients.find(p => p.id === parseInt(urlPatientId));
    if (patient?.postcode) {
      adminApi.geocodeBatch([patient.postcode]).then(response => {
        if (response.data[patient.postcode!]) {
          setHighlightedPatientCoords(response.data[patient.postcode!]);
        }
      }).catch(console.error);
    }
  }, [urlPatientId, patients]);

  // Get week days for date selector
  const weekDays = useMemo(() => {
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [selectedDate]);

  // Load physio coordinates
  useEffect(() => {
    if (!physios || physios.length === 0) return;

    const loadPhysioCoords = async () => {
      const postcodes = physios.map(p => p.postcode).filter(Boolean);
      if (postcodes.length === 0) return;

      try {
        const response = await adminApi.geocodeBatch(postcodes);
        const coordsMap: Record<number, Coordinates | null> = {};

        physios.forEach(physio => {
          if (physio.postcode && response.data[physio.postcode]) {
            coordsMap[physio.id] = response.data[physio.postcode];
          } else {
            coordsMap[physio.id] = null;
          }
        });

        setPhysioCoords(coordsMap);
      } catch (error) {
        console.error('Failed to geocode physio postcodes:', error);
      }
    };

    loadPhysioCoords();
  }, [physios]);

  // Load patient coordinates
  useEffect(() => {
    if (!patients || patients.length === 0) return;

    const loadPatientCoords = async () => {
      const postcodes = patients.map(p => p.postcode).filter(Boolean) as string[];
      if (postcodes.length === 0) return;

      try {
        const response = await adminApi.geocodeBatch(postcodes);
        const coordsMap: Record<number, Coordinates | null> = {};

        patients.forEach(patient => {
          if (patient.postcode && response.data[patient.postcode]) {
            coordsMap[patient.id] = response.data[patient.postcode];
          } else {
            coordsMap[patient.id] = null;
          }
        });

        setPatientCoords(coordsMap);
      } catch (error) {
        console.error('Failed to geocode patient postcodes:', error);
      }
    };

    loadPatientCoords();
  }, [patients]);

  // Filter appointments by date and physio
  const filteredAppointments = useMemo(() => {
    if (!appointments || !patients || !physios) return [];

    return appointments
      .filter(apt => {
        const aptDate = parseISO(apt.start_time);
        const matchesDate = isSameDay(aptDate, selectedDate);
        const matchesPhysio = selectedPhysio === 'all' || apt.physio_id === selectedPhysio;
        return matchesDate && matchesPhysio && apt.physio_id;
      })
      .map(apt => {
        const patient = patients.find(p => p.id === apt.patient_id);
        const physio = physios.find(p => p.id === apt.physio_id);
        return {
          id: apt.id,
          patient_id: apt.patient_id,
          patient_name: patient?.name || 'Unknown',
          patient_postcode: patient?.postcode || '',
          physio_id: apt.physio_id!,
          physio_name: physio?.name || 'Unknown',
          start_time: apt.start_time,
          end_time: apt.end_time,
          coords: patient ? patientCoords[patient.id] || null : null,
        };
      })
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  }, [appointments, patients, physios, selectedDate, selectedPhysio, patientCoords]);

  // Load route when physio is selected
  useEffect(() => {
    if (selectedPhysio === 'all' || !physios) {
      setRouteCoords([]);
      setRouteInfo(null);
      return;
    }

    const physio = physios.find(p => p.id === selectedPhysio);
    if (!physio || !physioCoords[physio.id]) {
      setRouteCoords([]);
      setRouteInfo(null);
      return;
    }

    const physioAppts = filteredAppointments.filter(a => a.physio_id === selectedPhysio && a.coords);
    if (physioAppts.length === 0) {
      setRouteCoords([]);
      setRouteInfo(null);
      return;
    }

    const loadRoute = async () => {
      setIsLoadingCoords(true);
      try {
        const points: Coordinates[] = [physioCoords[physio.id]!];
        physioAppts.forEach(apt => {
          if (apt.coords) {
            points.push(apt.coords);
          }
        });
        // Return to physio home
        points.push(physioCoords[physio.id]!);

        const response = await adminApi.getRoute(points);
        setRouteCoords(response.data.coordinates);
        setRouteInfo({
          distance_km: response.data.distance_km,
          duration_mins: response.data.duration_mins,
        });
      } catch (error) {
        console.error('Failed to load route:', error);
        // Fallback to straight lines
        const points: Array<[number, number]> = [];
        points.push([physioCoords[physio.id]!.lng, physioCoords[physio.id]!.lat]);
        physioAppts.forEach(apt => {
          if (apt.coords) {
            points.push([apt.coords.lng, apt.coords.lat]);
          }
        });
        points.push([physioCoords[physio.id]!.lng, physioCoords[physio.id]!.lat]);
        setRouteCoords(points);
      } finally {
        setIsLoadingCoords(false);
      }
    };

    loadRoute();
  }, [selectedPhysio, filteredAppointments, physios, physioCoords]);

  // Calculate map bounds
  const mapBounds = useMemo(() => {
    const allCoords: Array<[number, number]> = [];

    // Add physio coords
    Object.values(physioCoords).forEach(coord => {
      if (coord) allCoords.push([coord.lat, coord.lng]);
    });

    // Add appointment patient coords
    filteredAppointments.forEach(apt => {
      if (apt.coords) allCoords.push([apt.coords.lat, apt.coords.lng]);
    });

    // Add highlighted patient coords
    if (highlightedPatientCoords) {
      allCoords.push([highlightedPatientCoords.lat, highlightedPatientCoords.lng]);
    }

    if (allCoords.length === 0) return null;

    return L.latLngBounds(allCoords);
  }, [physioCoords, filteredAppointments, highlightedPatientCoords]);

  // Get physio color
  const getPhysioColor = (physioId: number) => {
    return physioColors[(physioId - 1) % physioColors.length];
  };

  // Group appointments by physio for the sidebar
  const appointmentsByPhysio = useMemo(() => {
    const groups: Record<number, AppointmentWithCoords[]> = {};
    filteredAppointments.forEach(apt => {
      if (!groups[apt.physio_id]) {
        groups[apt.physio_id] = [];
      }
      groups[apt.physio_id].push(apt);
    });
    return groups;
  }, [filteredAppointments]);

  // Determine where the cancelled appointment would fit in the schedule
  const appointmentFitInfo = useMemo(() => {
    if (!urlTime || !urlPatientId || selectedPhysio === 'all') return null;

    const cancelledTime = parseISO(urlTime);
    const physioAppts = filteredAppointments.filter(a => a.physio_id === selectedPhysio);

    // Find where this appointment would fit
    let beforeApt: AppointmentWithCoords | null = null;
    let afterApt: AppointmentWithCoords | null = null;

    for (const apt of physioAppts) {
      const aptTime = parseISO(apt.start_time);
      if (aptTime < cancelledTime) {
        beforeApt = apt;
      } else if (aptTime > cancelledTime && !afterApt) {
        afterApt = apt;
        break;
      }
    }

    return { beforeApt, afterApt, cancelledTime };
  }, [urlTime, urlPatientId, selectedPhysio, filteredAppointments]);

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4">
      {/* Sidebar */}
      <div className="w-80 flex flex-col gap-4 overflow-hidden">
        {/* Cancellation Context Banner */}
        {urlPatientId && urlTime && (
          <Card className="flex-shrink-0 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-amber-900 text-sm">Viewing Cancellation Fit</h4>
                <p className="text-xs text-amber-700 mt-1">
                  <span className="font-medium">{urlPatientName || 'Patient'}</span> at{' '}
                  <span className="font-medium">{format(parseISO(urlTime), 'HH:mm')}</span>
                </p>
                {appointmentFitInfo && (
                  <div className="mt-2 text-xs text-amber-700">
                    {appointmentFitInfo.beforeApt && (
                      <div>After: {appointmentFitInfo.beforeApt.patient_name} ({format(parseISO(appointmentFitInfo.beforeApt.end_time), 'HH:mm')})</div>
                    )}
                    {appointmentFitInfo.afterApt && (
                      <div>Before: {appointmentFitInfo.afterApt.patient_name} ({format(parseISO(appointmentFitInfo.afterApt.start_time), 'HH:mm')})</div>
                    )}
                    {!appointmentFitInfo.beforeApt && !appointmentFitInfo.afterApt && (
                      <div>This would be the only appointment</div>
                    )}
                  </div>
                )}
                {urlCancellationId && urlSuggestionId && (
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          await confirmSuggestion.mutateAsync({
                            cancellationId: parseInt(urlCancellationId),
                            suggestionId: parseInt(urlSuggestionId),
                          });
                          // Navigate to dashboard after successful confirmation
                          navigate('/', { state: { message: 'Appointment confirmed successfully!' } });
                        } catch (error) {
                          console.error('Failed to confirm suggestion:', error);
                        }
                      }}
                      isLoading={confirmSuggestion.isPending}
                    >
                      Confirm Booking
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => navigate(`/cancellations/${urlCancellationId}`)}
                    >
                      Back
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Date Selection */}
        <Card className="flex-shrink-0">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Select Day</h3>
          <div className="grid grid-cols-7 gap-1">
            {weekDays.map((day) => {
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelectedDate(day)}
                  className={`p-2 rounded-lg text-center transition-colors ${
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : isToday
                        ? 'bg-primary-100 text-primary-700'
                        : 'hover:bg-gray-100'
                  }`}
                >
                  <div className="text-xs font-medium">{format(day, 'EEE')}</div>
                  <div className="text-lg font-semibold">{format(day, 'd')}</div>
                </button>
              );
            })}
          </div>
        </Card>

        {/* Physio Filter */}
        <Card className="flex-shrink-0">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Filter by Physio</h3>
          <Select
            value={selectedPhysio.toString()}
            onChange={(e) => setSelectedPhysio(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
            options={[
              { value: 'all', label: 'All Physios' },
              ...(physios?.map((physio) => ({
                value: physio.id.toString(),
                label: physio.name,
              })) || []),
            ]}
          />

          {selectedPhysio !== 'all' && routeInfo && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Total Distance:</span>
                  <span className="font-medium">{routeInfo.distance_km} km</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Est. Travel Time:</span>
                  <span className="font-medium">{routeInfo.duration_mins} mins</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Appointments List */}
        <Card className="flex-1 overflow-hidden flex flex-col">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Appointments ({filteredAppointments.length})
          </h3>
          <div className="flex-1 overflow-y-auto space-y-3">
            {Object.entries(appointmentsByPhysio).map(([physioId, appts]) => {
              const physio = physios?.find(p => p.id === parseInt(physioId));
              const color = getPhysioColor(parseInt(physioId));

              return (
                <div key={physioId}>
                  <div
                    className="flex items-center gap-2 mb-2 cursor-pointer hover:opacity-80"
                    onClick={() => setSelectedPhysio(parseInt(physioId))}
                  >
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <span className="font-medium text-sm">{physio?.name}</span>
                    <span className="text-xs text-gray-500">({appts.length})</span>
                  </div>
                  <div className="space-y-1 ml-6">
                    {appts.map((apt, index) => (
                      <div
                        key={apt.id}
                        className="text-xs p-2 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                            style={{ backgroundColor: color }}
                          >
                            {index + 1}
                          </span>
                          <div>
                            <div className="font-medium">{apt.patient_name}</div>
                            <div className="text-gray-500">
                              {format(parseISO(apt.start_time), 'HH:mm')} - {format(parseISO(apt.end_time), 'HH:mm')}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {filteredAppointments.length === 0 && (
              <div className="text-center text-gray-500 text-sm py-4">
                No appointments for this day
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Map */}
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative min-h-[500px]">
        <MapContainer
          center={[51.2, -0.8]} // Default to Hampshire area
          zoom={10}
          style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <FitBounds bounds={mapBounds} />

          {/* Physio home markers */}
          {physios?.map((physio) => {
            const coords = physioCoords[physio.id];
            if (!coords) return null;

            const color = getPhysioColor(physio.id);
            const isSelected = selectedPhysio === 'all' || selectedPhysio === physio.id;

            if (!isSelected) return null;

            return (
              <Marker
                key={`physio-${physio.id}`}
                position={[coords.lat, coords.lng]}
                icon={createPhysioHomeIcon(color)}
              >
                <Tooltip direction="top" offset={[0, -20]} opacity={1}>
                  <div className="font-medium">{physio.name}</div>
                  <div className="text-sm text-gray-500">Home Base: {physio.postcode}</div>
                </Tooltip>
              </Marker>
            );
          })}

          {/* Appointment markers */}
          {filteredAppointments.map((apt) => {
            if (!apt.coords) return null;

            const color = getPhysioColor(apt.physio_id);
            const physioAppts = filteredAppointments.filter(a => a.physio_id === apt.physio_id);
            const aptIndex = physioAppts.findIndex(a => a.id === apt.id) + 1;

            return (
              <Marker
                key={`apt-${apt.id}`}
                position={[apt.coords.lat, apt.coords.lng]}
                icon={createNumberedPatientIcon(aptIndex, color)}
              >
                <Tooltip direction="top" offset={[0, -15]} opacity={1}>
                  <div className="font-medium">{apt.patient_name}</div>
                  <div className="text-sm text-gray-500">{apt.patient_postcode}</div>
                  <div className="text-sm">
                    {format(parseISO(apt.start_time), 'HH:mm')} - {format(parseISO(apt.end_time), 'HH:mm')}
                  </div>
                  <div className="text-sm text-primary-600">{apt.physio_name}</div>
                </Tooltip>
              </Marker>
            );
          })}

          {/* Highlighted patient marker (for cancellation context) */}
          {highlightedPatientCoords && urlPatientName && urlTime && (
            <Marker
              position={[highlightedPatientCoords.lat, highlightedPatientCoords.lng]}
              icon={createHighlightedPatientIcon()}
            >
              <Tooltip direction="top" offset={[0, -18]} opacity={1}>
                <div className="font-medium text-red-600">{urlPatientName}</div>
                <div className="text-sm font-medium">Proposed: {format(parseISO(urlTime), 'HH:mm')}</div>
                <div className="text-xs text-gray-500 mt-1">Cancelled appointment</div>
              </Tooltip>
            </Marker>
          )}

          {/* Route line for selected physio */}
          {selectedPhysio !== 'all' && routeCoords.length > 1 && (
            <Polyline
              positions={routeCoords.map(([lng, lat]) => [lat, lng])}
              color={getPhysioColor(selectedPhysio)}
              weight={4}
              opacity={0.7}
            />
          )}

          {/* Route lines for all physios when viewing all */}
          {selectedPhysio === 'all' && physios?.map((physio) => {
            const physioAppts = filteredAppointments.filter(a => a.physio_id === physio.id && a.coords);
            const physioHome = physioCoords[physio.id];

            if (!physioHome || physioAppts.length === 0) return null;

            // Create simple straight line route
            const routePoints: Array<[number, number]> = [
              [physioHome.lat, physioHome.lng],
              ...physioAppts.map(a => [a.coords!.lat, a.coords!.lng] as [number, number]),
              [physioHome.lat, physioHome.lng],
            ];

            return (
              <Polyline
                key={`route-${physio.id}`}
                positions={routePoints}
                color={getPhysioColor(physio.id)}
                weight={3}
                opacity={0.5}
                dashArray="5, 10"
              />
            );
          })}
        </MapContainer>

        {isLoadingCoords && (
          <div className="absolute top-4 right-4 bg-white px-3 py-2 rounded-lg shadow-md z-[1000]">
            <span className="text-sm text-gray-600">Loading route...</span>
          </div>
        )}
      </div>
    </div>
  );
}
